import { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import { IPage, IUser, IWorkOrder } from 'common/src/types/interfaces';

import { NEW_ID } from 'portal/src/constants/ReservedIDs';

import { deleteWorkOrder, getWorkOrders } from '../../api/WorkOrderService';
import DataTable from '../common/DataTable';
import addDateColumnProps from '../common/tables/addDateColumnProps';

function WorkOrders() {
  const navigate = useNavigate();

  const {
    data: workOrders,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['workOrders'],
    queryFn: async () => await getWorkOrders(),
    initialData: { data: [] } as IPage<IWorkOrder>,
  });

  const gridColumns: GridColDef[] = [
    { field: 'title', headerName: 'Title', flex: 1 },
    { field: 'status', headerName: 'Status', flex: 1 },
    { field: 'priority', headerName: 'Priority', flex: 1 },
    {
      field: 'dueDate',
      flex: 1,
      ...addDateColumnProps('Due Date'),
    },
    {
      field: 'assignee',
      headerName: 'Assignee',
      flex: 1,
      valueGetter: (value: IUser) => value?.name,
    },
    {
      field: 'createdAt',
      flex: 1,
      ...addDateColumnProps('Created At'),
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      flex: 1,
      valueGetter: (value: IUser) => value.name,
    },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 1,
      sortable: false,
      valueGetter: (value: string[]) => value?.join(', ') ?? '',
    },
  ];

  const handleNavigate = (id: string) => navigate(`./${id}`);

  return (
    <DataTable
      actionsConfig={{
        readActionConfig: {
          onClick: (id) => handleNavigate(id),
        },
        deleteConfig: {
          targetStr: 'work order',
          onDeleteClick: async (id) => {
            await deleteWorkOrder(id);
            await refetch();
          },
          snackbarMsgKey: 'WORK_ORDER',
        },
      }}
      columns={gridColumns}
      contextName={NESTED_ROUTES.ADMINISTRATION.WORK_ORDERS}
      dataFetchError={isError}
      loading={isFetching}
      rows={workOrders.data}
      toolbarSlotProps={{
        exportFileName: 'work_orders',
        onNewButtonClick: () => handleNavigate(NEW_ID),
        showFilters: true,
      }}
    />
  );
}

export default WorkOrders;
